/**
 * Nearby Leads Map — GPS-based lead proximity finder
 * Shows agent's location + nearby leads with distance calculations
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader } from '@googlemaps/js-api-loader';
import { Button } from '../components/ui/button';
import {
  MapPin, Navigation, RefreshCw, Loader2, ChevronRight,
  Phone, Mail, Clock, Building2, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

function timeAgo(dateStr) {
  if (!dateStr) return 'No activity';
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
}

export default function NearbyLeadsPage() {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);

  const [myLocation, setMyLocation] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geoLoading, setGeoLoading] = useState(false);
  const [radius, setRadius] = useState(10);
  const [selectedLead, setSelectedLead] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [geocodingAll, setGeocodingAll] = useState(false);

  // Get agent's GPS location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setMyLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        () => {
          // Default to a central US location if denied
          setMyLocation({ lat: 32.7767, lng: -96.7970 }); // Dallas
          toast.error('Location access denied. Showing default location.');
        },
        { enableHighAccuracy: true }
      );
    } else {
      setMyLocation({ lat: 32.7767, lng: -96.7970 });
    }
  }, []);

  // Load Google Maps
  useEffect(() => {
    if (!MAPS_KEY || !mapRef.current || mapLoaded) return;

    const loader = new Loader({
      apiKey: MAPS_KEY,
      version: 'weekly',
      libraries: ['places', 'geometry'],
    });

    loader.load().then((google) => {
      const center = myLocation || { lat: 32.7767, lng: -96.7970 };
      mapInstance.current = new google.maps.Map(mapRef.current, {
        center,
        zoom: 12,
        styles: [
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] },
        ],
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
      infoWindowRef.current = new google.maps.InfoWindow();
      setMapLoaded(true);
    }).catch(() => {
      toast.error('Failed to load Google Maps');
    });
  }, [myLocation, mapLoaded]);

  // Fetch nearby leads
  const fetchNearby = useCallback(async () => {
    if (!myLocation) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API}/api/geo/nearby?lat=${myLocation.lat}&lng=${myLocation.lng}&radius=${radius}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setLeads(data.leads || []);
      }
    } catch {
      toast.error('Failed to load nearby leads');
    } finally {
      setLoading(false);
    }
  }, [myLocation, radius]);

  useEffect(() => {
    if (myLocation) fetchNearby();
  }, [myLocation, radius, fetchNearby]);

  // Update map markers
  useEffect(() => {
    if (!mapInstance.current || !mapLoaded || !window.google) return;

    // Clear old markers
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    const google = window.google;

    // Agent location marker (blue)
    if (myLocation) {
      const agentMarker = new google.maps.Marker({
        position: myLocation,
        map: mapInstance.current,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: '#2563EB',
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 3,
        },
        title: 'Your Location',
        zIndex: 100,
      });

      // Pulsing ring
      new google.maps.Circle({
        map: mapInstance.current,
        center: myLocation,
        radius: 200,
        fillColor: '#2563EB',
        fillOpacity: 0.1,
        strokeColor: '#2563EB',
        strokeOpacity: 0.3,
        strokeWeight: 1,
      });

      markersRef.current.push(agentMarker);
      mapInstance.current.setCenter(myLocation);
    }

    // Lead markers
    leads.forEach((lead) => {
      const color = lead.status === 'qualified' ? '#10B981'
        : lead.status === 'contacted' ? '#F59E0B'
        : lead.status === 'new' ? '#8B5CF6'
        : '#94A3B8';

      const marker = new google.maps.Marker({
        position: { lat: lead.lat, lng: lead.lng },
        map: mapInstance.current,
        icon: {
          path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
          fillColor: color,
          fillOpacity: 0.9,
          strokeColor: '#fff',
          strokeWeight: 1.5,
          scale: 1.8,
          anchor: new google.maps.Point(12, 22),
        },
        title: lead.business_name,
      });

      marker.addListener('click', () => {
        setSelectedLead(lead);
        infoWindowRef.current.setContent(`
          <div style="padding:8px;max-width:220px;font-family:system-ui">
            <p style="font-weight:600;font-size:13px;margin:0 0 4px">${lead.business_name}</p>
            <p style="font-size:11px;color:#666;margin:0 0 2px">${lead.distance_miles} mi away</p>
            <p style="font-size:11px;color:#666;margin:0">${lead.address || ''}</p>
          </div>
        `);
        infoWindowRef.current.open(mapInstance.current, marker);
      });

      markersRef.current.push(marker);
    });

    // Fit bounds if leads exist
    if (leads.length > 0 && myLocation) {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(myLocation);
      leads.slice(0, 20).forEach(l => bounds.extend({ lat: l.lat, lng: l.lng }));
      mapInstance.current.fitBounds(bounds, 60);
    }
  }, [leads, myLocation, mapLoaded]);

  // Batch geocode
  const handleGeocodeAll = async () => {
    setGeocodingAll(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/geo/geocode-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Geocoded ${data.geocoded} leads`);
        fetchNearby();
      }
    } catch {
      toast.error('Geocoding failed');
    } finally {
      setGeocodingAll(false);
    }
  };

  return (
    <div className="h-[calc(100vh-64px)] flex" data-testid="nearby-leads-page">
      {/* Sidebar — Lead List */}
      <div className="w-[380px] border-r bg-card flex flex-col shrink-0">
        {/* Header */}
        <div className="p-4 border-b space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5 text-[#2563EB]" /> Nearby Leads
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {leads.length} leads within {radius} miles
              </p>
            </div>
            <div className="flex gap-1.5">
              <Button
                variant="outline"
                size="sm"
                onClick={handleGeocodeAll}
                disabled={geocodingAll}
                className="text-xs"
                data-testid="geocode-all-btn"
              >
                {geocodingAll ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                <span className="ml-1">Sync</span>
              </Button>
            </div>
          </div>

          {/* Radius Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-3.5 w-3.5 text-muted-foreground" />
            <div className="flex bg-muted rounded-lg p-0.5 flex-1">
              {[5, 10, 25, 50].map(r => (
                <button
                  key={r}
                  onClick={() => setRadius(r)}
                  data-testid={`radius-${r}`}
                  className={`flex-1 py-1.5 text-[11px] font-medium rounded-md transition-colors ${
                    radius === r ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {r} mi
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Lead List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12 px-6">
              <MapPin className="h-8 w-8 text-muted-foreground mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium">No leads nearby</p>
              <p className="text-xs text-muted-foreground mt-1">
                Try increasing the radius or sync your leads to geocode their addresses
              </p>
              <Button variant="outline" size="sm" className="mt-4" onClick={handleGeocodeAll}>
                <RefreshCw className="h-3 w-3 mr-1.5" /> Geocode Leads
              </Button>
            </div>
          ) : (
            leads.map((lead) => (
              <div
                key={lead.id}
                onClick={() => {
                  setSelectedLead(lead);
                  if (mapInstance.current) {
                    mapInstance.current.panTo({ lat: lead.lat, lng: lead.lng });
                    mapInstance.current.setZoom(15);
                  }
                }}
                data-testid={`nearby-lead-${lead.id}`}
                className={`px-4 py-3 border-b cursor-pointer transition-colors hover:bg-accent/50 ${
                  selectedLead?.id === lead.id ? 'bg-accent/70 border-l-2 border-l-[#2563EB]' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{lead.business_name}</p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{lead.address}</p>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <span className="text-sm font-bold text-[#2563EB]">{lead.distance_miles} mi</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  {lead.phone && (
                    <a href={`tel:${lead.phone}`} className="text-[10px] text-muted-foreground flex items-center gap-1 hover:text-foreground" onClick={e => e.stopPropagation()}>
                      <Phone className="h-3 w-3" /> {lead.phone}
                    </a>
                  )}
                  <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {timeAgo(lead.last_activity)}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                    lead.status === 'qualified' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400'
                    : lead.status === 'contacted' ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400'
                    : 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-400'
                  }`}>
                    {lead.status || 'new'}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[10px] px-2"
                    onClick={(e) => { e.stopPropagation(); navigate(`/leads/${lead.id}`); }}
                  >
                    View <ChevronRight className="h-3 w-3 ml-0.5" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Location Info */}
        {myLocation && (
          <div className="p-3 border-t bg-muted/30 text-center">
            <p className="text-[10px] text-muted-foreground flex items-center justify-center gap-1">
              <Navigation className="h-3 w-3 text-[#2563EB]" />
              {myLocation.lat.toFixed(4)}, {myLocation.lng.toFixed(4)}
            </p>
          </div>
        )}
      </div>

      {/* Map */}
      <div className="flex-1 relative">
        <div ref={mapRef} className="w-full h-full" data-testid="google-map" />
        {!MAPS_KEY && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted/80">
            <div className="text-center">
              <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="font-medium">Google Maps API Key Required</p>
              <p className="text-sm text-muted-foreground mt-1">Configure REACT_APP_GOOGLE_MAPS_KEY to enable the map</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
